import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { User } from '../../types/services/user';
import { login as apiLogin, logout as apiLogout, checkAuth as apiCheckAuth } from './authService';

type AuthContextType = {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  claims: User['claims'] | null;
  roles: User['claims']['roles'];
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const verify = async () => {
      try {
        const u = await apiCheckAuth();
        setUser(u);
      } catch (e) {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    verify();
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const u = await apiLogin({ email, password });
      setUser(u);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await apiLogout();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const refresh = async () => {
    setIsLoading(true);
    try {
      const u = await apiCheckAuth();
      setUser(u);
    } finally {
      setIsLoading(false);
    }
  };

  const claims = useMemo(() => (user ? user.claims : null), [user]);
  const roles = claims?.roles || [];

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    claims,
    roles,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe ser usado dentro de AuthProvider');
  return ctx;
};